// src/main.jsx
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";

import Login from "./pages/Login.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import GerencialDashboard from "./pages/GerencialDashboard.jsx";
import ContractForm from "./pages/ContractForm.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import Menu from "./pages/Menu.jsx";
import Analitica from "./pages/Analitica.jsx";
import PlanDesarrollo from "./pages/PlanDesarrollo.jsx";
import Presupuesto from "./pages/presupuesto.jsx";
import SeguimientoIndicador from "./pages/SeguimientoIndicador.jsx";
import TransformadorCSV from "./pages/TransformadorCSV.jsx";
import HaciendaPresupuesto from "./pages/HaciendaPresupuesto.jsx";
import Indicadores from "./pages/Indicadores.jsx";
import SolicitudAdquisicionesMenu from "./pages/submenu.jsx";
import SolicitudAdquisiciones from "./pages/SolicitudAdquisiciones.jsx";
import InexistenciaFirmar from "./pages/InexistenciaFirmar.jsx";
import SeguimientoPDD from "./pages/SeguimientoPDD.jsx";
import SeguimientoPDDGlobal from "./pages/SeguimientoPDDGlobal.jsx";
import RevisionPDD from "./pages/RevisionPDD.jsx";
import RevisionIndicador from "./pages/RevisionIndicador.jsx";

// ── Otras necesidades CDP/RP ──────────────────────────────────────────────────
import OtrasNecesidadesCDPRP from "./pages/OtrasNecesidadesCDPRP.jsx";
import SolicitadosCDPRP from "./pages/SolicitadosCDPRP.jsx";
import FormularioCDPExtra from "./pages/FormularioCDPExtra.jsx";
import FormularioRPExtra from "./pages/FormularioRPExtra.jsx";

import AlmacenSolicitudes from "./components/AlmacenSolicitudes.jsx";
import AdquisicionBienesPage from "./components/AdquisicionBienes.jsx";
import InexistenciaSolicitudes from "./components/InexistenciaSolicitudes.jsx";

import IndicadoresLey from "./pages/IndicadoresLey";
import ConcejoHonorarios from "./pages/ConcejoHonorarios";
import PersoneriaIndicadores from "./pages/PersoneriaIndicadores";
import IndicadorLey9993 from "./pages/IndicadorLey9993.jsx";
import SeguimientoPresupuestal from "./pages/SeguimientoPresupuestal";
import Piip from "./pages/Piip";
import ProyeccionIngresos from "./pages/ProyeccionIngresos";
import ProyeccionVigenciaActual from "./pages/ProyeccionVigenciaActual";
import PACIngresos from "./pages/PACIngresos.jsx";
import RecursosBalance from "./pages/RecursosBalance.jsx";
import CierreFiscal from "./pages/CierreFiscal.jsx";

import "./styles/main.css";
import "./styles/pdf-styles.css";

const clearLocalSession = () => {
  localStorage.removeItem("auth_user");
  localStorage.removeItem("auth_token");
  sessionStorage.removeItem("auth_user");
  sessionStorage.removeItem("auth_token");
  sessionStorage.removeItem("post_login_path");
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("token");
};

const getStoredUser = () => {
  try {
    const raw =
      localStorage.getItem("auth_user") ||
      sessionStorage.getItem("auth_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    clearLocalSession();
    return null;
  }
};

const hasValidLocalSession = () => {
  const token =
    localStorage.getItem("auth_token") ||
    sessionStorage.getItem("auth_token");
  const u = getStoredUser();
  return Boolean(token && u);
};

const getRoleValue = (u) => {
  const name = (u?.role ?? u?.roleName ?? u?.rol ?? "")
    .toString()
    .trim();
  if (name) return name.toLowerCase();
  const id =
    u?.role_id ?? u?.rolId ?? u?.roleId ?? u?.rol_id ?? null;
  return id != null ? String(id) : "";
};

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div style={{ padding: 16 }}>Verificando autenticación…</div>;
  }

  const isResetPath = location.pathname === "/reset-password";
  const isLoginPath = location.pathname === "/login";
  const isAuthed = Boolean(user) || hasValidLocalSession();

  if (!isAuthed && !isResetPath && !isLoginPath) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function ForceMenuByRole() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div style={{ padding: 16 }}>Verificando autenticación…</div>;
  }

  const effectiveUser = user || getStoredUser();
  if (!effectiveUser) return <Navigate to="/login" replace />;

  const roleId = Number(
    effectiveUser?.role_id ??
      effectiveUser?.rolId ??
      effectiveUser?.roleId ??
      effectiveUser?.rol_id ??
      NaN
  );

  if (roleId === 13) return <Navigate to="/hacienda-presupuesto" replace />;
  if (roleId === 15) return <Navigate to="/gerencia" replace />;
  if (roleId === 16) return <Navigate to="/seguimiento-pdd" replace />;
  if (roleId === 17) return <Navigate to="/seguimiento-pdd-global" replace />;
  if (roleId === 18) return <Navigate to="/otras-necesidades-cdp-rp" replace />;

  return <Menu />;
}

function LogoutRoute() {
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    clearLocalSession();
    window.location.replace("/login");
  }, []);

  return <div style={{ padding: 16 }}>Cerrando sesión…</div>;
}

function InexistenciaSolicitudesRoute() {
  const { user } = useAuth();
  const effectiveUser = user || (hasValidLocalSession() ? getStoredUser() : null);
  const role = getRoleValue(effectiveUser);

  const roleId = Number(
    effectiveUser?.role_id ??
      effectiveUser?.rolId ??
      effectiveUser?.roleId ??
      effectiveUser?.rol_id ??
      NaN
  );

  if (![9, 10].includes(roleId)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <InexistenciaSolicitudes role={role} />;
}

function HomeRedirect() {
  const { user } = useAuth();
  const localOk = hasValidLocalSession();
  const u = user || (localOk ? getStoredUser() : null);

  if (!u) return <Navigate to="/login" replace />;

  const roleId = Number(
    u?.role_id ?? u?.rolId ?? u?.roleId ?? u?.rol_id ?? NaN
  );

  if (roleId === 8)  return <Navigate to="/almacen/solicitudes" replace />;
  if ([9, 10].includes(roleId)) return <Navigate to="/inexistencia/solicitudes" replace />;
  if (roleId === 11) return <Navigate to="/dashboard" replace />;
  if ([2, 3, 4, 5, 6, 7].includes(roleId)) return <Navigate to="/dashboard" replace />;
  if (roleId === 13) return <Navigate to="/hacienda-presupuesto" replace />;
  if (roleId === 15) return <Navigate to="/gerencia" replace />;
  if (roleId === 16) return <Navigate to="/seguimiento-pdd" replace />;
  if (roleId === 17) return <Navigate to="/seguimiento-pdd-global" replace />;
  if (roleId === 18) return <Navigate to="/otras-necesidades-cdp-rp" replace />;

  return <Navigate to="/menu" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-shell">
          <main className="app-main">
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<LogoutRoute />} />

              <Route path="/reset-password" element={<ProtectedRoute><ResetPassword /></ProtectedRoute>} />
              <Route path="/menu" element={<ProtectedRoute><ForceMenuByRole /></ProtectedRoute>} />

              {/* ── Otras necesidades CDP/RP ── */}
              <Route path="/otras-necesidades-cdp-rp" element={<ProtectedRoute><OtrasNecesidadesCDPRP /></ProtectedRoute>} />
              <Route path="/otras-necesidades-cdp-rp/solicitados" element={<ProtectedRoute><SolicitadosCDPRP /></ProtectedRoute>} />
              <Route path="/otras-necesidades-cdp-rp/nuevo-cdp" element={<ProtectedRoute><FormularioCDPExtra /></ProtectedRoute>} />
              <Route path="/otras-necesidades-cdp-rp/nuevo-rp" element={<ProtectedRoute><FormularioRPExtra /></ProtectedRoute>} />

              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/gerencia" element={<ProtectedRoute><GerencialDashboard /></ProtectedRoute>} />
              <Route path="/analitica" element={<ProtectedRoute><Analitica /></ProtectedRoute>} />
              <Route path="/plan-desarrollo" element={<ProtectedRoute><PlanDesarrollo /></ProtectedRoute>} />

              <Route path="/seguimiento-pdd" element={<ProtectedRoute><SeguimientoPDD /></ProtectedRoute>} />
              <Route path="/seguimiento-pdd/indicador/:id" element={<ProtectedRoute><SeguimientoIndicador /></ProtectedRoute>} />
              <Route path="/seguimiento-pdd-global" element={<ProtectedRoute><SeguimientoPDDGlobal /></ProtectedRoute>} />
              <Route path="/revision-pdd" element={<ProtectedRoute><RevisionPDD /></ProtectedRoute>} />
              <Route path="/revision-pdd/indicador/:id" element={<ProtectedRoute><RevisionIndicador /></ProtectedRoute>} />

              <Route path="/plan-desarrollo-indicadores" element={<ProtectedRoute><Indicadores /></ProtectedRoute>} />
              <Route path="/presupuesto" element={<ProtectedRoute><Presupuesto /></ProtectedRoute>} />
              <Route path="/contratos/nuevo" element={<ProtectedRoute><ContractForm /></ProtectedRoute>} />
              <Route path="/contratos/:id" element={<ProtectedRoute><ContractForm /></ProtectedRoute>} />

              <Route path="/solicitud-adquisiciones" element={<ProtectedRoute><SolicitudAdquisicionesMenu /></ProtectedRoute>} />
              <Route path="/solicitud-adquisiciones/solicitudes" element={<ProtectedRoute><SolicitudAdquisiciones /></ProtectedRoute>} />

              <Route path="/almacen/solicitudes" element={<ProtectedRoute><AlmacenSolicitudes /></ProtectedRoute>} />
              <Route path="/adquisicion/:id" element={<ProtectedRoute><AdquisicionBienesPage /></ProtectedRoute>} />

              <Route path="/inexistencia/solicitudes" element={<ProtectedRoute><InexistenciaSolicitudesRoute /></ProtectedRoute>} />
              <Route path="/inexistencia/:id" element={<ProtectedRoute><InexistenciaFirmar /></ProtectedRoute>} />

              <Route path="/hacienda-presupuesto" element={<ProtectedRoute><HaciendaPresupuesto /></ProtectedRoute>} />
              <Route path="/hacienda-presupuesto/transformador" element={<ProtectedRoute><TransformadorCSV /></ProtectedRoute>} />
              <Route path="/hacienda-presupuesto/indicadores-ley" element={<ProtectedRoute><IndicadoresLey /></ProtectedRoute>} />
              <Route path="/hacienda-presupuesto/concejo-honorarios" element={<ProtectedRoute><ConcejoHonorarios /></ProtectedRoute>} />
              <Route path="/hacienda-presupuesto/personeria" element={<ProtectedRoute><PersoneriaIndicadores /></ProtectedRoute>} />
              <Route path="/hacienda-presupuesto/ley-99-93" element={<ProtectedRoute><IndicadorLey9993 /></ProtectedRoute>} />
              <Route path="/hacienda-presupuesto/seguimiento" element={<ProtectedRoute><SeguimientoPresupuestal /></ProtectedRoute>} />
              <Route path="/hacienda-presupuesto/piip" element={<Piip />} />
              <Route path="/hacienda-presupuesto/proyeccion-ingresos" element={<ProyeccionIngresos />} />
              <Route path="/hacienda-presupuesto/proyeccion-vigencia-actual" element={<ProyeccionVigenciaActual />} />
              <Route path="/hacienda-presupuesto/pac-ingresos" element={<PACIngresos />} />
              <Route path="/hacienda-presupuesto/recursos-balance" element={<RecursosBalance />} />
              <Route path="/hacienda-presupuesto/cierre-fiscal" element={<CierreFiscal />} />

              <Route path="*" element={<HomeRedirect />} />
            </Routes>
          </main>

          <footer className="app-footer">
            <p>© {new Date().getFullYear()} - Alcaldía Municipal</p>
          </footer>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);